import { keyframes } from '@emotion/react';
import { t } from '@lingui/core/macro';
import { alpha, Collapse, styled, useMediaQuery } from '@mui/material';
import { ChevronRightIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, SearchIcon } from 'lucide-react';
import * as React from 'react';

import { type AppLinkOptions, useCanOpen } from '../../routing';
import { linkable } from '../../routing/links/linkable';
import { asLinkProps } from '../../routing/links/linkOptions';
import { useCurrentEntry } from '../../routing/links/useCurrentEntry';
import { body, colorSystem, h5 } from '../../theme';
import { Divider } from '../Divider';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { ReleaseTag } from '../ReleaseTag';
import { Tooltip } from '../Tooltip';
import { useAppSidebarPeek } from './useAppSidebarPeek';

/** A page the sidebar links to. `activeFor`: the pages it stands for, when more than its own and those beneath it. */
export type AppSidebarPage = { label: string; link: AppLinkOptions; activeFor?: ReadonlyArray<AppLinkOptions> };

/**
 * A first-level entry: a page, or an area whose pages show under it while one of them is the page on screen. `tag`: a
 * word at the end of such a page's row, such as "Beta". `group`: the heading of the pages it starts, and of the ones
 * after it with the same group; a gap sets it apart from the pages above. An area's `link`: where it leads, if not to
 * its first page. `blockStart`: a line above the entry, which starts a block of entries.
 */
export type AppSidebarEntry = { blockStart?: boolean } & (
    | (AppSidebarPage & { icon: IconElement })
    | {
          label: string;
          icon: IconElement;
          link?: AppLinkOptions;
          pages: ReadonlyArray<AppSidebarPage & { tag?: string; group?: string }>;
      }
);

export type AppSidebarProps = {
    /**
     * The row at the top: a logo that links to the start page. `label` names the link; the image is decoration, drawn
     * 131 px wide and 18 px high, the wordmark's shape. The rail shows its first 18 px: the mark at its start.
     */
    logo: { src: string; label: string; link: AppLinkOptions };
    /**
     * The row below it. `shortcut` shows beside the label, and in the rail's tooltip; `keys` is the same in the form of
     * `aria-keyshortcuts`, such as "Control+K", for a screen reader.
     */
    search: { label: string; shortcut: string; keys: string; onSearch: () => void };
    /**
     * A link below the search, such as the Lumi Agent's, set apart by an accent. `shortcut` and `keys` as the search's;
     * the app binds the shortcut. With `onToggle`, a plain click calls it instead, to show the agent beside the page,
     * and a click with a modifier key still opens the link in a new tab; `isOpen` marks the row while the agent shows.
     */
    agent?: {
        label: string;
        icon: IconElement;
        shortcut: string;
        keys: string;
        link: AppLinkOptions;
        isOpen?: boolean;
        onToggle?: () => void;
    };
    entries: ReadonlyArray<AppSidebarEntry>;
    /** A row of the app's own below the footer entries, such as the bell: an `AppSidebarButton`. */
    notifications?: React.ReactNode;
    /** Entries at the bottom; the page on screen is looked up among them, `entries` and `agent` together. */
    footerEntries?: ReadonlyArray<AppSidebarEntry>;
    /** The last row, below the collapse button, such as the user's menu: an `AppSidebarButton`. */
    user?: React.ReactNode;
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
    /** The shortcut the app binds to collapsing, such as "Ctrl+B": the collapse button names it. */
    collapseShortcut: string;
};

/** Wide enough for German labels such as "Lieferantenstrategie". */
const OPEN_WIDTH = 224;
/** 40px targets beside 8px of padding on each side. */
const RAIL_WIDTH = 56;
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN = 'cubic-bezier(0.64, 0, 0.8, 0)';
/** Opening and collapsing, the sidebar's and an area's pages': one speed for both. */
const MOTION_MS = 250;
const PEEK_IN_MS = 200;
const PEEK_OUT_MS = 150;

// The peek's surface comes from the rail's side and goes back to it. A short way: the rows on it stay where they are.
const away = { opacity: 0, transform: 'translateX(-16px)' };
const slideIn = keyframes({ from: away });
const slideOut = keyframes({ to: away });

/** Whether the sidebar is a rail, for the rows' tooltips. */
const RailContext = React.createContext(false);
/** Short: in the rail a row's label shows only as its tooltip, and an area's pages only in its flyout. */
const ROW_TOOLTIP_DELAY_MS = 100;
const rowTooltip = {
    placement: 'right',
    describeChild: true,
    enterDelay: ROW_TOOLTIP_DELAY_MS,
    // MUI shows a tooltip at once after another one, such as a button's on the page.
    enterNextDelay: ROW_TOOLTIP_DELAY_MS,
} as const;

/**
 * The app's main navigation, controlled: the menu comes as data, and the sidebar shows what the session may open
 * (`useCanOpen`) and marks the entry of the page on screen. Collapsed, it is a rail of icons whose labels, and the open
 * area's pages, stay in the DOM for screen readers; the labels show as tooltips. Every row keeps its place between the
 * two, so nothing jumps: the rail keeps the room of what only shows when open, an area's pages. Its width moves between the two, unless the user asks for reduced motion, and the page beside it follows,
 * reflowing on each frame.
 * In the rail an area's icon shows its pages in a flyout under the pointer. Under the keyboard's focus the rail peeks:
 * it shows as when open, over the page, which stays where it is (`useAppSidebarPeek`). Open, an area's chevron shows
 * or hides its pages, which otherwise show while one of them is the page on screen.
 */
export function AppSidebar({
    logo,
    search,
    agent: givenAgent,
    entries,
    notifications,
    footerEntries = [],
    user,
    collapsed,
    onCollapsedChange,
    collapseShortcut,
}: AppSidebarProps): JSX.Element {
    const canOpen = useCanOpen();
    const agent = givenAgent !== undefined && canOpen(givenAgent.link) ? givenAgent : undefined;
    const top = visibleEntries(entries, canOpen);
    const bottom = visibleEntries(footerEntries, canOpen);
    const pages = [
        ...(agent === undefined ? [] : [agent]),
        ...[...top, ...bottom].flatMap((entry) => ('pages' in entry ? entry.pages : [entry])),
    ];
    const current = useCurrentEntry(pages);
    // Only the sidebar's own motion: the app's theme keeps its transitions.
    const isStill = useMediaQuery('(prefers-reduced-motion: reduce)');
    const nav = React.useRef<HTMLElement>(null);
    const movingFocus = React.useRef(false);
    const peek = useAppSidebarPeek(nav, collapsed, isStill ? 0 : PEEK_OUT_MS, movingFocus);
    // Peeking, it is laid out as when open.
    const isRail = collapsed && peek === undefined;
    const view: EntryView = {
        current: current && { page: pages[current.index], exact: current.exact },
        motionMs: isStill ? 0 : MOTION_MS,
    };
    const motion = `${MOTION_MS}ms ${EASE_OUT}`;

    // The rail doesn't show an area's pages (`data-rail-hides`): the keyboard's focus would be out of sight on a page.
    // It moves to what the rail shows in their place: the page's area, filled. A click's focus may stay: there it would
    // show no ring.
    const collapseButton = React.useRef<HTMLButtonElement>(null);
    const focused = document.activeElement;
    // Any area's pages: those of one that closes stay a moment beside those of the one that opened.
    const hidden = isRail && focused?.matches(':focus-visible') ? focused.closest('[data-rail-hides]') : null;
    // The area's entry is the first link of its row.
    const focusTarget = hidden === null ? null : (hidden.closest('li')?.querySelector('a') ?? collapseButton.current);
    React.useLayoutEffect(() => {
        if (focusTarget !== null) {
            movingFocus.current = true;
            focusTarget.focus();
            movingFocus.current = false;
        }
    }, [focusTarget]);

    const collapseLabel = collapsed ? t`Expand sidebar` : t`Collapse sidebar`;

    return (
        <RailContext.Provider value={isRail}>
            <Nav
                ref={nav}
                aria-label={t`Main`}
                data-collapsed={isRail ? '' : undefined}
                data-peek={peek}
                // What shows of it moves only when it is pinned from the peek: it stays as wide as the peek meanwhile.
                style={
                    isStill ? undefined : { transition: `width ${motion}${collapsed ? '' : `, clip-path ${motion}`}` }
                }
            >
                <Rows style={{ gap: '4px' }}>
                    <LogoRow>
                        <LogoAnchor {...asLinkProps(logo.link)} aria-label={logo.label}>
                            {/* The wordmark's 653×90 at 18px: the link has its width before the image loads. */}
                            <img alt="" src={logo.src} width={131} height={18} />
                        </LogoAnchor>
                    </LogoRow>
                    <Tooltip title={isRail ? `${search.label} ${search.shortcut}` : ''} {...rowTooltip}>
                        <SearchButton
                            type="button"
                            aria-haspopup="dialog"
                            aria-keyshortcuts={search.keys}
                            onClick={search.onSearch}
                        >
                            <Icon aria-hidden>
                                <SearchIcon />
                            </Icon>
                            <Label>{search.label}</Label>
                            <Kbd aria-hidden>{search.shortcut}</Kbd>
                        </SearchButton>
                    </Tooltip>
                    {agent !== undefined && (
                        <Tooltip title={isRail ? `${agent.label} ${agent.shortcut}` : ''} {...rowTooltip}>
                            <AgentAnchor
                                {...asLinkProps(agent.link)}
                                aria-keyshortcuts={agent.keys}
                                aria-expanded={agent.onToggle && agent.isOpen}
                                // It shows the agent beside the page, not a popup: the peek mustn't stay open for it.
                                data-disclosure
                                data-active={view.current?.page === agent || agent.isOpen ? 'page' : undefined}
                                current={ariaCurrent(agent, view)}
                                onClick={(event: React.MouseEvent) => {
                                    const isPlain = !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
                                    if (agent.onToggle && isPlain && event.button === 0) {
                                        event.preventDefault();
                                        agent.onToggle();
                                    }
                                }}
                            >
                                <AgentIcon aria-hidden>{agent.icon}</AgentIcon>
                                <Label>{agent.label}</Label>
                                <Kbd aria-hidden>{agent.shortcut}</Kbd>
                            </AgentAnchor>
                        </Tooltip>
                    )}
                </Rows>
                <Scrolling>
                    <EntryList entries={top} view={view} />
                </Scrolling>
                <BottomLine />
                <Rows>
                    {bottom.length > 0 && <EntryList entries={bottom} view={view} />}
                    {notifications}
                    <Tooltip
                        title={
                            isRail ? (
                                <>
                                    {collapseLabel}
                                    <Shortcut>{collapseShortcut}</Shortcut>
                                </>
                            ) : (
                                ''
                            )
                        }
                        {...rowTooltip}
                    >
                        <RowButton ref={collapseButton} type="button" onClick={() => onCollapsedChange(!collapsed)}>
                            <Icon aria-hidden>{collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}</Icon>
                            <Label>{collapseLabel}</Label>
                            <Kbd aria-hidden>{collapseShortcut}</Kbd>
                        </RowButton>
                    </Tooltip>
                    {user}
                </Rows>
            </Nav>
        </RailContext.Provider>
    );
}

/**
 * A button drawn as an entry, for the rows only the app can fill, such as the bell and the user's menu. Its label is
 * its tooltip in the rail, and where it is cut short. `icon` is decoration, and what is wider than an icon, such as an
 * avatar, is centred on its column. A row that opens a popup must set `aria-expanded`: without it, the collapsed
 * sidebar's peek closes when the focus moves into the popup.
 */
export function AppSidebarButton({
    icon,
    label,
    ...props
}: { icon: React.ReactNode; label: string } & Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>): JSX.Element {
    const { tooltip, labelRef } = useRowTooltip(label);
    return (
        <Tooltip {...tooltip}>
            <RowButton type="button" {...props}>
                <Icon aria-hidden>{icon}</Icon>
                <Label ref={labelRef}>{label}</Label>
            </RowButton>
        </Tooltip>
    );
}

/**
 * Drops what the user may not open; an area keeps the pages they may, and goes without any. An area whose own link they
 * may not open leads to its first page.
 */
function visibleEntries(
    entries: ReadonlyArray<AppSidebarEntry>,
    canOpen: (link: AppLinkOptions) => boolean,
): AppSidebarEntry[] {
    return entries.flatMap((entry): AppSidebarEntry[] => {
        if (!('pages' in entry)) {
            return canOpen(entry.link) ? [entry] : [];
        }
        const pages = entry.pages.filter((page) => canOpen(page.link));
        const { link, ...area } = entry;
        return pages.length > 0 ? [{ ...area, ...(link && canOpen(link) && { link }), pages }] : [];
    });
}

type AreaPage = AppSidebarPage & { tag?: string; group?: string };

type EntryView = {
    /** The entry of the page on screen, and whether the page is its link's exactly. */
    current: { page: AppSidebarPage; exact: boolean } | undefined;
    /** How long an area's pages take to open and to close. */
    motionMs: number;
};

function EntryList({ entries, view }: { entries: AppSidebarEntry[]; view: EntryView }): JSX.Element {
    return (
        <List>
            {entries.map((entry) => (
                // shortcut: a hidden block's first entry takes its line along; carry it to the next one if that matters.
                <li key={entry.label} data-block-start={entry.blockStart ? '' : undefined}>
                    {'pages' in entry ? <Area area={entry} view={view} /> : <Page page={entry} view={view} />}
                </li>
            ))}
        </List>
    );
}

function Page({ page, view }: { page: AppSidebarPage & { icon: IconElement }; view: EntryView }): JSX.Element {
    return (
        <EntryLink
            link={page.link}
            label={page.label}
            icon={page.icon}
            active={view.current?.page === page ? 'page' : undefined}
            current={ariaCurrent(page, view)}
        />
    );
}

/** The page's own entry is the page; one it sits below is filled the same, and current, but not the page. */
function ariaCurrent(page: AppSidebarPage, { current }: EntryView): 'page' | 'true' | undefined {
    if (current?.page !== page) {
        return undefined;
    }
    return current.exact ? 'page' : 'true';
}

/**
 * Links to its own link, or else to its first page; its pages show under it while one of them is current. The rail keeps their room as a track,
 * a row for each: no row below may move under the pointer when the rail opens. It keeps the pages too, out of sight, so
 * a screen reader reads on below the area as when open, and a page that takes the focus is the one the peek shows.
 */
function Area({ area, view }: { area: Extract<AppSidebarEntry, { pages: unknown }>; view: EntryView }): JSX.Element {
    const isRail = React.useContext(RailContext);
    const holdsCurrent = view.current !== undefined && area.pages.includes(view.current.page);
    // The user's choice holds until the page on screen moves into the area or out of it.
    const [chosen, setChosen] = React.useState<boolean>();
    const [heldCurrent, setHeldCurrent] = React.useState(holdsCurrent);
    if (heldCurrent !== holdsCurrent) {
        setHeldCurrent(holdsCurrent);
        setChosen(undefined);
    }
    const isOpen = chosen ?? holdsCurrent;
    const headingIds = React.useId();
    const pagesId = React.useId();
    // Runs of pages under one heading, among the pages the user may open, so a group keeps its heading without its
    // first page. A page without a group is a run of its own, without a heading.
    const runs = area.pages.reduce<Array<{ heading: string | undefined; pages: AreaPage[] }>>((all, page) => {
        const last = all.at(-1);
        if (last !== undefined && page.group !== undefined && page.group === last.heading) {
            last.pages.push(page);
        } else {
            all.push({ heading: page.group, pages: [page] });
        }
        return all;
    }, []);
    return (
        <>
            <AreaRow>
                <EntryLink
                    link={area.link ?? area.pages[0].link}
                    label={area.label}
                    icon={area.icon}
                    active={holdsCurrent ? 'area' : undefined}
                    flyout={<Flyout label={area.label} runs={runs} view={view} />}
                />
                {/* Not a popup's: the peek mustn't stay open for it (`data-disclosure`). */}
                <Toggle
                    type="button"
                    data-disclosure
                    // Out of sight in the rail, as the pages are: the focus moves from it to the area's entry.
                    data-rail-hides
                    aria-expanded={isOpen}
                    aria-controls={pagesId}
                    aria-label={isOpen ? t`Hide the pages of ${area.label}` : t`Show the pages of ${area.label}`}
                    onClick={() => setChosen(!isOpen)}
                >
                    <ChevronRightIcon size={16} />
                </Toggle>
            </AreaRow>
            {isRail && isOpen && (
                <Track data-track aria-hidden>
                    {runs.flatMap(({ heading, pages }, index) => [
                        ...(heading === undefined
                            ? []
                            : [
                                  <li
                                      key={`heading-${index}`}
                                      data-heading
                                      data-group-start={index > 0 ? '' : undefined}
                                  />,
                              ]),
                        ...pages.map((page) => (
                            <li key={page.label} data-current={view.current?.page === page ? '' : undefined} />
                        )),
                    ])}
                </Track>
            )}
            {/* Mounted open, it is simply there, on load; it stays in the rail. One that opens later expands. */}
            <Pages in={isOpen} unmountOnExit timeout={view.motionMs} easing={EASE_OUT}>
                <List id={pagesId} data-rail-hides style={{ margin: `${PAGES_MARGIN} 0 ${AREA_END_MARGIN}` }}>
                    {runs.map(({ heading, pages }, index) =>
                        heading === undefined ? (
                            pages.map((page) => (
                                <li key={page.label}>
                                    <PageLink page={page} view={view} />
                                </li>
                            ))
                        ) : (
                            <Group key={`group-${index}`} data-group-start={index > 0 ? '' : undefined}>
                                <Heading id={`${headingIds}-${index}`}>
                                    <Label>{heading}</Label>
                                </Heading>
                                <List aria-labelledby={`${headingIds}-${index}`}>
                                    {pages.map((page) => (
                                        <li key={page.label}>
                                            <PageLink page={page} view={view} />
                                        </li>
                                    ))}
                                </List>
                            </Group>
                        ),
                    )}
                </List>
            </Pages>
        </>
    );
}

/** An area's pages beside its icon in the rail, under their headings, with the page on screen marked. */
function Flyout({
    label,
    runs,
    view,
}: {
    label: string;
    runs: Array<{ heading: string | undefined; pages: AreaPage[] }>;
    view: EntryView;
}): JSX.Element {
    return (
        <FlyoutMenu aria-label={label}>
            <FlyoutTitle>{label}</FlyoutTitle>
            {runs.map(({ heading, pages }, index) => (
                <React.Fragment key={index}>
                    {heading !== undefined && <FlyoutHeading>{heading}</FlyoutHeading>}
                    {pages.map((page) => (
                        <FlyoutAnchor
                            key={page.label}
                            {...asLinkProps(page.link)}
                            data-active={view.current?.page === page ? 'page' : undefined}
                            current={ariaCurrent(page, view)}
                        >
                            {page.label}
                        </FlyoutAnchor>
                    ))}
                </React.Fragment>
            ))}
        </FlyoutMenu>
    );
}

function PageLink({ page, view }: { page: AppSidebarPage & { tag?: string }; view: EntryView }): JSX.Element {
    const { tooltip, labelRef } = useRowTooltip(page.label);
    return (
        <Tooltip {...tooltip}>
            <PageAnchor
                {...asLinkProps(page.link)}
                data-active={view.current?.page === page ? 'page' : undefined}
                current={ariaCurrent(page, view)}
            >
                <Label ref={labelRef}>{page.label}</Label>
                {page.tag !== undefined && (
                    <PageTag>
                        <ReleaseTag color="violet" label={page.tag} />
                    </PageTag>
                )}
            </PageAnchor>
        </Tooltip>
    );
}

/** An entry with an icon. */
function EntryLink({
    link,
    label,
    icon,
    active,
    current,
    flyout,
}: {
    link: AppLinkOptions;
    label: string;
    icon: IconElement;
    active?: 'page' | 'area';
    current?: 'page' | 'true';
    /** What the rail shows beside the entry in place of its label, such as an area's pages. */
    flyout?: JSX.Element;
}): JSX.Element {
    const isRail = React.useContext(RailContext);
    const { tooltip, labelRef } = useRowTooltip(label);
    // An entry with a flyout keeps the white tooltip open too: another variant is another component, which would
    // remount the link and lose its focus as the rail opens or closes.
    const shown =
        flyout === undefined
            ? tooltip
            : isRail
              ? {
                    ...tooltip,
                    title: flyout,
                    variant: 'white' as const,
                    placement: 'right-start' as const,
                    // Hover intent before it opens, and time to reach it, as NN/g's timings for content on hover.
                    enterDelay: 300,
                    enterNextDelay: 300,
                    leaveDelay: 500,
                }
              : { ...tooltip, variant: 'white' as const };
    return (
        // One element in the rail and open, so focus stays.
        <Tooltip {...shown}>
            <EntryAnchor {...asLinkProps(link)} data-active={active} current={current}>
                <Icon aria-hidden>{icon}</Icon>
                <Label ref={labelRef}>{label}</Label>
            </EntryAnchor>
        </Tooltip>
    );
}

/**
 * A row's label as its tooltip, which describes the row rather than naming it: in the rail, where the label is out of
 * sight, and where the open sidebar cuts it short. An empty title never opens.
 */
function useRowTooltip(label: string): {
    tooltip: typeof rowTooltip & { title: string; onOpen: () => void };
    labelRef: React.RefObject<HTMLSpanElement>;
} {
    const isRail = React.useContext(RailContext);
    const labelRef = React.useRef<HTMLSpanElement>(null);
    const [isCut, setIsCut] = React.useState(false);
    // Measured as it would open: only a long language, or a tag beside it, cuts a label short. The rail cuts it to
    // nothing. Asked of the DOM: MUI calls the `onOpen` of the render its wait began in, which may have been the rail's
    // when the peek has opened since.
    const onOpen = () => {
        const text = labelRef.current;
        setIsCut(text?.closest('[data-collapsed]') === null && text.scrollWidth > text.clientWidth);
    };
    return { tooltip: { ...rowTooltip, title: isRail || isCut ? label : '', onOpen }, labelRef };
}

/** Sets `aria-current` itself: the router marks every link whose route matches, the sidebar only the page. */
const AnchorHost = React.forwardRef<
    HTMLAnchorElement,
    React.ComponentPropsWithoutRef<'a'> & { current: 'page' | 'true' | undefined }
>(function AnchorHost({ current, children, ...props }, ref) {
    return (
        <a ref={ref} {...props} aria-current={current}>
            {children}
        </a>
    );
});

const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
} as const;

const Nav = styled('nav')(({ theme }) => ({
    // Above the page, which the peek reaches over, and so does the sidebar while it is pinned from the peek.
    position: 'relative',
    zIndex: theme.zIndex.drawer,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: OPEN_WIDTH,
    height: '100%',
    padding: '12px 8px',
    background: colorSystem.neutral[1],
    // Opening, its rows take the open width at once, so no line wraps, and its edge uncovers them; collapsing, they take
    // the rail's layout at once.
    clipPath: 'inset(0)',
    '&:not([data-collapsed]) > *': {
        width: OPEN_WIDTH - 2 * 8,
    },
    // The surface the rows show on, which only the peek, and the sidebar pinned from it, let reach over the page.
    '&::before': {
        content: '""',
        position: 'absolute',
        zIndex: -1,
        top: 0,
        bottom: 0,
        left: 0,
        width: OPEN_WIDTH,
        background: colorSystem.neutral[1],
    },
    '&[data-collapsed], &[data-peek]': {
        width: RAIL_WIDTH,
    },
    // The peek keeps the rail's width, so the page stays where it is, and its rows reach over the page onto the surface.
    '&[data-peek]': {
        // Past the surface and its shadow. An inset still: pinned, it moves in to the edge as the edge moves out, and
        // so stays past the surface.
        clipPath: `inset(0 -${OPEN_WIDTH}px 0 0)`,
        '&::before': {
            boxShadow: `1px 0 0 ${colorSystem.neutral[2]}, 8px 0 24px rgba(42, 49, 71, 0.12)`,
            '@media (prefers-reduced-motion: no-preference)': {
                animation: `${slideIn} ${PEEK_IN_MS}ms ${EASE_OUT}`,
            },
        },
    },
    '&[data-peek="closing"]::before': {
        animation: `${slideOut} ${PEEK_OUT_MS}ms ${EASE_IN} forwards`,
    },
}));

/** What the rail hides goes with the surface while the peek is `closing`, a phase that reduced motion skips. */
const PEEK_OUT = `${PEEK_OUT_MS}ms ${EASE_IN}`;
const leaving = { opacity: 0, transition: `opacity ${PEEK_OUT}` };

/** Between the main list and the rows at the bottom; as the peek closes, it shortens to the rail's with the surface. */
const BottomLine = styled(Divider)({
    'nav[data-peek="closing"] > &': {
        width: RAIL_WIDTH - 2 * 8,
        transition: `width ${PEEK_OUT}`,
    },
});

const Rows = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
});

const Scrolling = styled('div')({
    flex: '1 1 auto',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
});

const List = styled('ul')({
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    // Between an area's groups of pages; the rail's track is a `List` too, so its rows keep the same room.
    '& > li[data-group-start]': {
        marginTop: '8px',
    },
    '& > li[data-block-start]': {
        marginTop: '6px',
        paddingTop: '8px',
        borderTop: `1px solid ${colorSystem.neutral[2]}`,
    },
    // As wide as the peek, it would stay over the page once its surface is gone.
    'nav[data-peek="closing"] & > li[data-block-start]': {
        borderTopColor: 'transparent',
        transition: `border-color ${PEEK_OUT}`,
    },
});

/** The icon column, 16px wide in every row; what is wider is centred on it. */
const Icon = styled('span')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    width: '16px',
    ...iconSlot(16, { reach: 'wrapped' }),
});

const Label = styled('span')({
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '[data-collapsed] &': { ...visuallyHidden, opacity: 0 },
    '@media (prefers-reduced-motion: no-preference)': { transition: `opacity 120ms ${EASE_OUT}` },
    '[data-peek="closing"] &': leaving,
});

/** A row: 32px high, its icon 12px from its edge, so the icons line up, and stay where they are in the rail. */
const rowStyle = {
    ...body,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxSizing: 'border-box',
    height: '32px',
    padding: '0 12px',
    borderRadius: '8px',
    color: colorSystem.neutral[7],
    textDecoration: 'none',
    // Half of neutral 2: all of it outshone the white fill of the page's own entry, and white would look like it.
    '&:hover': {
        background: alpha(colorSystem.neutral[2], 0.5),
        color: colorSystem.neutral[9],
    },
    '&:focus-visible': {
        outline: `2px solid ${colorSystem.primary[5]}`,
        outlineOffset: '-2px',
    },
    '&[data-active]': {
        color: colorSystem.neutral[9],
        fontWeight: 560,
    },
    // The page's own entry is filled; an open area is only emphasised, except in the rail, where its pages don't show.
    '&[data-active="page"], [data-collapsed] &[data-active]': {
        background: colorSystem.neutral.white,
        boxShadow: `0 1px 2px rgba(42, 49, 71, 0.08), 0 0 0 1px ${colorSystem.neutral[2]}`,
    },
    // A fill as wide as the peek would be left on the page once its surface is gone.
    '[data-peek="closing"] &': {
        background: 'none',
        boxShadow: 'none',
        transition: `background-color ${PEEK_OUT}, box-shadow ${PEEK_OUT}`,
    },
} as const;

const EntryAnchor = linkable(styled(AnchorHost)(rowStyle));

const PAGE_HEIGHT = 28;
const HEADING_HEIGHT = '24px';
const PAGES_MARGIN = '2px';
/** After an area's last page, so the entry below doesn't read as one more of them. */
const AREA_END_MARGIN = '8px';
/** In line with the area's label: the row's 12px, the icon's 16px and the gap's 8px. */
const PAGE_INDENT = '36px';

const PageAnchor = linkable(
    styled(AnchorHost)({
        ...rowStyle,
        height: `${PAGE_HEIGHT}px`,
        paddingLeft: PAGE_INDENT,
    }),
);

/** A group of an area's pages under its heading, which names the list of them; as far apart as the rows. */
const Group = styled('li')({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
});

const Heading = styled('div')({
    ...h5,
    display: 'flex',
    alignItems: 'flex-end',
    boxSizing: 'border-box',
    height: HEADING_HEIGHT,
    padding: `0 12px 4px ${PAGE_INDENT}`,
    color: colorSystem.neutral[7],
});

/** Never narrower: a long label is cut short first. A box of its own, so the entry's name has a space before it. */
const PageTag = styled('span')({
    display: 'flex',
    flexShrink: 0,
    '[data-peek="closing"] &': leaving,
});

/** An area's pages, which the rail keeps out of sight and out of its layout: their track keeps their room. */
const Pages = styled(Collapse)({
    '[data-collapsed] &': visuallyHidden,
});

/**
 * The room of an area's pages in the rail: a line down the icon column, with the place of the page on screen. Placed
 * from the left, as the icons are, so it stays on that column while the width moves.
 */
const Track = styled(List)({
    position: 'relative',
    margin: `${PAGES_MARGIN} 0 ${AREA_END_MARGIN}`,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '6px',
        bottom: '6px',
        // The icon's centre: the row's 12px of padding and half the icon's 16px, less half the line's 2px.
        left: '19px',
        width: '2px',
        borderRadius: '1px',
        background: colorSystem.neutral[3],
    },
    '& > li': {
        height: `${PAGE_HEIGHT}px`,
    },
    '& > li[data-heading]': {
        height: HEADING_HEIGHT,
    },
    // A darker stretch of the line: a pill on it would read as a scrollbar's thumb.
    '& > li[data-current]::before': {
        content: '""',
        display: 'block',
        position: 'relative',
        width: '2px',
        height: `${PAGE_HEIGHT - 12}px`,
        margin: '6px 0 6px 19px',
        borderRadius: '1px',
        background: colorSystem.neutral[7],
    },
});

/** An area's entry with its chevron at the end, over the room the label leaves it. */
const AreaRow = styled('div')({
    position: 'relative',
    // Only while open: the rail is narrower than the room.
    'nav:not([data-collapsed]) & > a': { paddingRight: '36px' },
});

/** Points at the pages while they are hidden and turns down to them while they show. Open only: the rail has none. */
const Toggle = styled('button')({
    position: 'absolute',
    top: '4px',
    right: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    borderRadius: '6px',
    background: 'none',
    color: colorSystem.neutral[6],
    cursor: 'pointer',
    '&:hover': {
        background: colorSystem.neutral[2],
        color: colorSystem.neutral[9],
    },
    '&:focus-visible': {
        outline: `2px solid ${colorSystem.primary[5]}`,
        outlineOffset: '-2px',
    },
    '& > svg': {
        '@media (prefers-reduced-motion: no-preference)': { transition: `transform ${MOTION_MS}ms ${EASE_OUT}` },
    },
    '&[aria-expanded="true"] > svg': { transform: 'rotate(90deg)' },
    '[data-collapsed] &': { display: 'none' },
    '[data-peek="closing"] &': leaving,
});

const FlyoutMenu = styled('nav')({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '168px',
    padding: '4px 0',
});

const FlyoutTitle = styled('div')({
    ...body,
    padding: '2px 8px 6px',
    color: colorSystem.neutral[9],
    fontWeight: 560,
});

const FlyoutHeading = styled('div')({
    ...h5,
    padding: '8px 8px 2px',
    color: colorSystem.neutral[7],
});

const FlyoutAnchor = linkable(
    styled(AnchorHost)({
        ...body,
        display: 'block',
        padding: '6px 8px',
        borderRadius: '6px',
        color: colorSystem.neutral[7],
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: alpha(colorSystem.neutral[2], 0.5),
            color: colorSystem.neutral[9],
        },
        '&:focus-visible': {
            outline: `2px solid ${colorSystem.primary[5]}`,
            outlineOffset: '-2px',
        },
        '&[data-active]': {
            background: colorSystem.neutral[1],
            color: colorSystem.neutral[9],
            fontWeight: 560,
        },
    }),
);

const RowButton = styled('button')({
    ...rowStyle,
    width: '100%',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
});

/** A field while open, an icon in the rail: one button, so it keeps the focus between the two. */
const SearchButton = styled(RowButton)({
    'nav:not([data-collapsed], [data-peek="closing"]) &': {
        background: colorSystem.neutral.white,
        boxShadow: `0 0 0 1px ${colorSystem.neutral[2]}`,
        '&:hover': {
            boxShadow: `0 0 0 1px ${colorSystem.neutral[4]}`,
        },
    },
});

/**
 * The search's field, its icon the accent; the accent fills it on its page or while the agent shows, as white fills the
 * other entries.
 */
const AgentAnchor = linkable(
    styled(AnchorHost)({
        ...rowStyle,
        'nav:not([data-collapsed], [data-peek="closing"]) &': {
            background: colorSystem.neutral.white,
            boxShadow: `0 0 0 1px ${colorSystem.neutral[2]}`,
            '&:hover': {
                boxShadow: `0 0 0 1px ${colorSystem.primary[4]}`,
            },
            '&[data-active]': {
                background: colorSystem.primary[1],
                boxShadow: `0 0 0 1px ${colorSystem.primary[4]}`,
            },
        },
    }),
    { allowOnClick: true },
);

const AgentIcon = styled(Icon)({
    color: colorSystem.primary[6],
});

const Kbd = styled('kbd')({
    fontFamily: 'inherit',
    fontSize: '12px',
    // Regular on the page's own row too, whose label is emphasised.
    fontWeight: body.fontWeight,
    '[data-collapsed] &': { display: 'none' },
    '[data-peek="closing"] &': leaving,
});

/** The logo in line with the icons, as high as a row. */
const LogoRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    padding: '0 12px',
});

/**
 * In the rail, and as the peek slides out, only the wordmark's mark shows, where it is when open: collapsing, the
 * logo neither moves nor changes its size.
 */
const LogoAnchor = linkable(
    styled('a')({
        display: 'flex',
        flexShrink: 0,
        borderRadius: '4px',
        '& > img': { flexShrink: 0 },
        '[data-collapsed] &, [data-peek="closing"] &': { width: '18px', overflow: 'hidden' },
        '&:focus-visible': {
            outline: `2px solid ${colorSystem.primary[5]}`,
            outlineOffset: '2px',
        },
    }),
);

const Shortcut = styled('span')({
    marginLeft: '8px',
    opacity: 0.7,
});
